import { EvaluationStarsDataWrapper } from '../Dashboard.styles';
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { fetchEvaluationStarsThunk } from '../../../store/thunk/reportingThunk';
import { Box, Typography, IconButton } from '@mui/material';
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { useEvaluationStarsData } from '../../../store/selectors/reportingSelectors';
import { OptionContainer, OptionRow, ActionRow, ChartContainer, NoDataText } from './Widget.styles';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, RadialBarChart, RadialBar, Legend } from 'recharts';
import { chartColorList } from "../constants";
import { GeneralButton } from '../../../common/ui/GeneralButton';

export const EvaluationStarsChart = () => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [emptyChartData, setEmptyChartData] = useState<boolean>(true);
  const evaluationStarsData = useEvaluationStarsData();
  const dispatch = useAppDispatch();

  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    setStartDate(newDate?.format('YYYY-MM-DD') || null);
  };

  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    setEndDate(newDate?.format('YYYY-MM-DD') || null);
  };

  const fetchChartData = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      await dispatch(fetchEvaluationStarsThunk({ startDate, endDate }));
      setEmptyChartData(!evaluationStarsData || evaluationStarsData.length === 0 ||
        (evaluationStarsData[0].responseRate === 0 && evaluationStarsData[0].averageStars === 0));
    } catch (error) {
      console.error("Error fetching evaluation stars data:", error);
      setEmptyChartData(true);
    } finally {
      setLoading(false);
    }
  };

  const resetChartData = () => {
    setEmptyChartData(true);
  };

  const evaluationData = useMemo(() => evaluationStarsData[0] || { responseRate: 0, averageStars: 0 }, [evaluationStarsData]);

  const barData = [
    { name: t("dashboardPage.evaluationStars.responseRate"), value: evaluationData.responseRate },
  ];

  const radialData = [
    { name: t("dashboardPage.evaluationStars.averageStars"), value: evaluationData.averageStars },
  ];

  return (
    <>
      <OptionContainer>
        <OptionRow>
          <DatePicker
            label={t("dashboardPage.startDate")}
            value={startDate ? dayjs(startDate) : null}
            onChange={handleStartDateChange}
            format="DD.MM.YYYY"
            slotProps={{ textField: { size: "small", sx: { width: '35%', margin: '0 5px' } } }}
          />
          <DatePicker
            label={t("dashboardPage.endDate")}
            value={endDate ? dayjs(endDate) : null}
            onChange={handleEndDateChange}
            format="DD.MM.YYYY"
            slotProps={{ textField: { size: "small", sx: { width: '35%', margin: '0 5px' } } }}
          />
          <IconButton
            onClick={resetChartData}
            aria-label={t("buttons.reset")}
            sx={{ marginRight: 1, color: "#555" }}
            size="small"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </OptionRow>

        <ActionRow>
          <GeneralButton
            appVariant="widget"
            onClick={fetchChartData}
            disabled={!startDate || !endDate}
          >
            {t("buttons.apply")}
          </GeneralButton>
        </ActionRow>
      </OptionContainer>

      {loading ? (
        <ChartContainer>
          <NoDataText>{t("dashboardPage.loadingData")}</NoDataText>
        </ChartContainer>
      ) : emptyChartData ? (
        <ChartContainer>
          <NoDataText>{t("dashboardPage.noDataAvailable")}</NoDataText>
        </ChartContainer>
      ) : (
        <ChartContainer>
          <EvaluationStarsDataWrapper>
            <Box>
              <Typography variant="h6">
                {t("dashboardPage.evaluationStars.title")}
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={barData} layout="vertical">
                  <XAxis type="number" domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill={chartColorList[0]} />
                </BarChart>
              </ResponsiveContainer>
              <ResponsiveContainer width="100%" height={200}>
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius={20}
                  outerRadius={80}
                  barSize={20}
                  data={radialData}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar dataKey="value" fill={chartColorList[1]} />
                  <Tooltip />
                  <Legend />
                </RadialBarChart>
              </ResponsiveContainer>
            </Box>
          </EvaluationStarsDataWrapper>
        </ChartContainer>
      )}
    </>
  );
};

export default EvaluationStarsChart;
